import { PlanType } from '@/services/billing/plans';

/** The plan fields the checkout totals read (a structural subset of the billing `Plan`). */
export type CheckoutTotalsPlan = {
  planType: PlanType;
  basePrice: number;
  annualPrice: number;
  isPaidAnnually: boolean;
  cdpPremiumPrice?: number;
  billingTotals?: {
    due_today?: number | null;
    due_today_pre_savings?: number | null;
  };
};

/** The coupon fields the discount reads (Stripe and Ordway coupons both carry these). */
export type CheckoutTotalsCoupon = {
  amount_off?: number | null;
  percent_off?: number | null;
};

export type CheckoutTotalsInput = {
  plan: CheckoutTotalsPlan;
  coupon?: CheckoutTotalsCoupon;
  /** The tax rate as a fraction (e.g. 0.2 for 20%), or undefined when unknown. */
  taxRatePercent?: number;
  /** Contact/customer overage in cents (Essentials only). */
  customerOverageCents: number;
  /** Object-profile overage in cents (Essentials only). */
  objectOverageCents: number;
};

export type CheckoutTotals = {
  /** The base plan price (annual/base, plus CDP premium), never negative. In cents. */
  planPrice: number;
  discount: number;
  subtotal: number;
  /** The tax amount, or undefined when the tax rate is unknown. */
  tax?: number;
  total: number;
  /** The Ordway billing-totals "due today", or null for non-Ordway plans. */
  dueToday: number | null;
  /** The pre-savings amount shown struck-through, or null when it equals `dueToday`. */
  dueTodayPreSavings: number | null;
};

/** The base plan price: the annual or base price, overridden by the Ordway due-today, plus CDP premium. */
function planPriceFor(plan: CheckoutTotalsPlan): number {
  let price = plan.isPaidAnnually ? plan.annualPrice : plan.basePrice;
  if (plan.billingTotals?.due_today != null) {
    // `billing_totals` is already in integer cents — the backend builds it from `parseDollarsCents`
    // (services `clients/ordway/subscription.go`), unlike the plan's `list_price`/`effective_price`
    // (raw Ordway dollars that `ordwaySubscriptionToBillingPlan` scales ×100). So no conversion here.
    price = plan.billingTotals.due_today;
  }
  const total = price + (plan.cdpPremiumPrice ?? 0);
  return total < 0 ? 0 : total;
}

/** The coupon discount against the plan price: a fixed amount off, or a percentage of the price. */
function discountFor(planPrice: number, coupon: CheckoutTotalsCoupon | undefined): number {
  if (coupon == null) {
    return 0;
  }
  if (coupon.amount_off != null && coupon.amount_off > 0) {
    return coupon.amount_off;
  }
  if (coupon.percent_off != null && coupon.percent_off > 0) {
    return planPrice * (coupon.percent_off / 100);
  }
  return 0;
}

/** The pre-savings amount, shown struck-through only when the Ordway totals discount it from due-today. */
function dueTodayPreSavingsFor(plan: CheckoutTotalsPlan): number | null {
  const totals = plan.billingTotals;
  if (totals?.due_today_pre_savings == null || totals.due_today_pre_savings === totals.due_today) {
    return null;
  }
  return totals.due_today_pre_savings;
}

/**
 * Computes the checkout summary amounts (all in integer cents) from the plan, an optional coupon, the
 * tax rate, and the account's usage overages. Ports the Ember signup-form getters (planPrice,
 * discount, subtotal, tax, total, dueToday, dueTodayPreSavings). Usage overages are added only for
 * Essentials, matching Ember.
 */
export function computeCheckoutTotals(input: CheckoutTotalsInput): CheckoutTotals {
  const planPrice = planPriceFor(input.plan);
  const discount = discountFor(planPrice, input.coupon);

  const subtotal = Math.max(planPrice - discount, 0);
  const tax = input.taxRatePercent != null ? subtotal * input.taxRatePercent : undefined;

  const usage = input.plan.planType === PlanType.Essentials ? input.customerOverageCents + input.objectOverageCents : 0;
  const total = subtotal + (tax ?? 0) + usage;

  return {
    planPrice,
    discount,
    subtotal,
    tax,
    total,
    dueToday: input.plan.billingTotals?.due_today ?? null,
    dueTodayPreSavings: dueTodayPreSavingsFor(input.plan),
  };
}
